import { ClientContact } from "@jugl-web/rest-api";
import { PlainButton } from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import React from "react";
import { collapsedIconButtonClassName } from "../../../../consts";
import { ReactComponent as ArrowRightIcon } from "../../assets/arrow-right.svg";
import { ReactComponent as PlusIcon } from "../../assets/plus.svg";
import { ContactPickerField } from "../../components/ContactPickerField";
import { IdentifyContactDialogFooter } from "../../components/IdentifyContactDialogFooter";
import { useIdentifyContact } from "../../hooks/useIdentifyContact";
import { IdentifyContactDialogScreenMap } from "../../types";

export const LinkExistingContactScreen: React.FC<{
  selectedContact?: ClientContact;
  onSelectContact: (contact: ClientContact) => void;
  onRequestClose: () => void;
  onContactIdentified?: (contact: ClientContact) => void;
}> = ({
  selectedContact,
  onSelectContact,
  onRequestClose,
  onContactIdentified,
}) => {
  const { t } = useTranslations();
  const { transitionTo } =
    useScreenTransitionManager<IdentifyContactDialogScreenMap>();
  const { handleUpdateContact, isSubmitting } = useIdentifyContact();

  const handleSave = async () => {
    if (!selectedContact) {
      return;
    }

    const identifiedContact = await handleUpdateContact(selectedContact);

    if (identifiedContact) {
      if (onContactIdentified) {
        onContactIdentified(identifiedContact);
      } else {
        onRequestClose();
      }
    }
  };

  return (
    <>
      <div className="flex flex-col gap-8 px-14 pt-10 pb-12">
        <div className="flex gap-4">
          <div className="w-px shrink-0 rounded-full bg-[#E0E0E0]" />
          <div className="flex flex-col gap-1">
            <span className="font-secondary font-medium text-dark">
              {t({
                id: "client-support-page.visitor-not-in-customers",
                defaultMessage: "This visitor isn't in your Customers yet",
              })}
            </span>
            <span className="font-secondary text-[13px] text-dark-600">
              {t({
                id: "client-support-page.link-or-create-customer",
                defaultMessage:
                  "Link this conversation to an existing customer or create a new one",
              })}
            </span>
          </div>
        </div>
        <ContactPickerField
          selectedContact={selectedContact}
          onSelect={onSelectContact}
        />
        <PlainButton
          className={cx(
            collapsedIconButtonClassName,
            "flex h-8 w-max items-center justify-center gap-1.5 px-2.5 font-secondary text-sm font-medium text-[#1A2023]"
          )}
          onClick={() => transitionTo({ name: "createContact" })}
        >
          {t({
            id: "client-support-page.create-new-customer",
            defaultMessage: "Create New Customer",
          })}
          <PlusIcon />
        </PlainButton>
      </div>
      <IdentifyContactDialogFooter
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            variant: "tertiary",
            className: "w-[120px]",
            onClick: onRequestClose,
            isDisabled: isSubmitting,
          },
          {
            text: t({
              id: "common.save",
              defaultMessage: "Save",
            }),
            iconEnd: <ArrowRightIcon />,
            className: "w-[300px]",
            onClick: handleSave,
            isDisabled: !selectedContact || isSubmitting,
          },
        ]}
      />
    </>
  );
};
